import React, { useState, useEffect } from "react";
import { getImages } from "../projectImageSlider/ProjectImageSlider";
import ProjectLanguages from "../projectLanguages/ProjectLanguages";
import ProjectModal from "../projectModal/ProjectModal";
import "./ProjectShowcase.css";

const AUTO_DELAY = 6000;
const IMAGE_SLIDE_DELAY = 3500;

function ShowcaseBgSlider({ images, projectKey, exiting }) {
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setImgIdx(0);
  }, [projectKey]);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = setInterval(() => {
      setImgIdx((i) => (i + 1) % images.length);
    }, IMAGE_SLIDE_DELAY);
    return () => clearInterval(id);
  }, [images, projectKey]);

  if (images.length === 0) return null;

  return (
    <div className={`ps-bg-slider${exiting ? " ps-bg-slider--out" : ""}`}>
      <div
        className="ps-bg-track"
        style={{ transform: `translateX(-${imgIdx * 100}%)` }}
      >
        {images.map((src, i) => (
          <div
            key={`${projectKey}-${i}`}
            className="ps-bg-slide"
            style={{ backgroundImage: `url(${src})` }}
          />
        ))}
      </div>
      {images.length > 1 && (
        <div className="ps-img-dots">
          {images.map((_, i) => (
            <span
              key={i}
              className={`ps-img-dot${i === imgIdx ? " ps-img-dot--active" : ""}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectShowcase({ repos, theme }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const transition = (idx) => {
    if (idx === activeIdx || exiting) return;
    setExiting(true);
    setTimeout(() => {
      setActiveIdx(idx);
      setExiting(false);
    }, 260);
  };

  const goNext = () => {
    if (exiting) return;
    const next = (activeIdx + 1) % repos.length;
    setExiting(true);
    setTimeout(() => {
      setActiveIdx(next);
      setExiting(false);
    }, 260);
  };

  const goPrev = () => {
    if (exiting) return;
    const prev = (activeIdx - 1 + repos.length) % repos.length;
    setExiting(true);
    setTimeout(() => {
      setActiveIdx(prev);
      setExiting(false);
    }, 260);
  };

  // Auto-cycle: restarts whenever activeIdx or hover state changes
  useEffect(() => {
    if (isHovering || exiting) return;
    const id = setTimeout(() => {
      const next = (activeIdx + 1) % repos.length;
      setExiting(true);
      setTimeout(() => {
        setActiveIdx(next);
        setExiting(false);
      }, 260);
    }, AUTO_DELAY);
    return () => clearTimeout(id);
  }, [activeIdx, isHovering, exiting, repos.length]);

  const activeRepo = repos[activeIdx];
  const images = getImages(activeRepo?.screenshotsFolder);

  return (
    <div
      className="ps-wrap"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* ── Left: showcase panel ── */}
      <div className="ps-left">
        <ShowcaseBgSlider
          images={images}
          projectKey={activeRepo?.id ?? activeIdx}
          exiting={exiting}
        />
        <div className="ps-vignette" />

        {/* Prev / Next arrows */}
        <button className="ps-nav ps-nav--prev" onClick={goPrev} aria-label="Previous project">
          &#8249;
        </button>
        <button className="ps-nav ps-nav--next" onClick={goNext} aria-label="Next project">
          &#8250;
        </button>

        {/* Auto-cycle progress bar */}
        <div className="ps-progress-bar">
          <div
            key={`prog-${activeIdx}`}
            className={`ps-progress-fill${isHovering ? " ps-progress-fill--paused" : ""}`}
          />
        </div>

        <div className={`ps-info${exiting ? " ps-info--out" : ""}`}>
          <h2 className="ps-title">{activeRepo.name}</h2>
          <p className="ps-desc">{activeRepo.description}</p>
          <div className="ps-langs">
            <ProjectLanguages
              logos={activeRepo.languages}
              theme={{ ...theme, text: "rgba(255,255,255,0.85)" }}
            />
          </div>
          {activeRepo.details && (
            <button className="ps-cta" onClick={() => setShowModal(true)}>
              View Details →
            </button>
          )}
        </div>
      </div>

      {/* ── Right: project list ── */}
      <div className="ps-right">
        {repos.map((repo, i) => {
          const isActive = i === activeIdx;
          return (
            <div
              key={repo.id}
              className={`ps-item${isActive ? " ps-item--active" : ""}`}
              style={{
                animationDelay: `${i * 60}ms`,
                ...(isActive
                  ? {
                      backgroundColor:
                        theme.name === "dark"
                          ? "rgba(255,255,255,0.07)"
                          : "rgba(0,0,0,0.05)",
                    }
                  : {}),
              }}
              onClick={() => transition(i)}
            >
              <div className="ps-item__body">
                <span
                  className="ps-item__name"
                  style={{ color: isActive ? theme.text : theme.secondaryText }}
                >
                  {repo.name}
                </span>
              </div>
              <span className={`ps-item__arrow${isActive ? " ps-item__arrow--on" : ""}`}>
                →
              </span>
            </div>
          );
        })}
      </div>

      {showModal && (
        <ProjectModal
          repo={activeRepo}
          theme={theme}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
